/* eslint-disable camelcase */
import moment from 'moment'
import {
  Button as MuiButton,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid as MuiGrid,
  IconButton,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { AxiosError, AxiosResponse } from 'axios'
import { BatchDialog } from '../BatchDialog'
import { plainToClass } from 'class-transformer'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import React, { createRef, ReactNode, useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { ParcelOrder, ParcelOrderUnit, Sku } from 'src/api/models'
import { AttachmentProp } from 'src/components/Dialog/AddAttachmentsDialog'
import { FilePreview, UrlPreview } from 'src/components/FilePreview'
import { Paginator, useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import styled from 'styled-components'
import * as yup from 'yup'

import {
  useForm,
  AddressField,
  BooleanField,
  DateField,
  NumberField,
  QuerySelectField,
  TextareaField,
  TextField,
} from 'src/forms'

const Centered = styled.div`
  text-align: center;
`

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface CustomFieldProps {
  name: string
  value: string
}

interface SkuProps {
  sku: string
  num_units: number // eslint-disable-line camelcase
}

interface AddressProps {
  company?: string
  consignee: string
  phone?: string
  address1: string
  address2?: string
  address3?: string
  city: string
  state: string
  postal_code: string // eslint-disable-line camelcase
}

interface OrderProps {
  number: string
  carrier?: string
  details: string
  fulfill_on: string
  address: AddressProps
  custom_fields: CustomFieldProps[] // eslint-disable-line camelcase
  inventory: SkuProps[]
  attachments?: AttachmentProp[]
}

const orderSchema = yup.object().shape({
  number: yup
    .string()
    .nullable()
    .label('Order Number')
    .transform((value) => (value === '' ? undefined : value)),
  carrier: yup.string().optional().nullable().label('Carrier'),
  fulfill_on: yup
    .date()
    .optional()
    .nullable()
    .default(null),
  details: yup
    .string()
    .label('Order Details').nullable()
    .transform((value) => (value === '' ? undefined : value)),
  address: yup
    .object({
      company: yup.string().label('Company').optional(),
      consignee: yup.string().label('Consignee').required(),
      phone: yup.string().label('Phone Number').optional().nullable(),
      address1: yup.string().label('Address 1').required(),
      address2: yup.string().label('Address 2'),
      address3: yup.string().label('Address 3'),
      city: yup.string().label('City').required(),
      state: yup.string().label('State').required(),
      postal_code: yup.string().label('Postal Code').required(),
    })
    .required(),
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
  inventory: yup.array().of(
    yup
      .object({
        sku: yup.string().required().label('SKU'),
        num_units: yup
          .number()
          .required()
          .label('Number of Units')
          .transform((currentValue, originalValue) => {
            if (currentValue === '' || originalValue === '') {
              return undefined
            }
            return currentValue
          }),
      })
      .label('Inventory')
  ),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  order?: ParcelOrder
}

interface ImageListWrapperProps {
  children: ReactNode
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

interface InventoryListRowProps {
  value: any
  skuPaginator: Paginator
  listItemName: string
  onRemove: (arg0: string) => void
}
const InventoryListRow = (props: InventoryListRowProps) => {
  const { skuPaginator, listItemName, onRemove } = props
  const batchPaginator = useQueryPaginator(
    'batch-list',
    skuit.client.batches.list,
    {
      params: {},
    },
  )
  return <Grid container spacing={2} key={listItemName}>
    <Grid item xs={4}>
      <QuerySelectField
        name="sku"
        label="[SKU] Product"
        paginator={skuPaginator}
        getOptionLabel={(sku) => `|${sku?.sku}| ${sku?.product?.name}`}
        renderOption={(option) => {
          return (
            <Grid container spacing={2} m={2}>
              <Grid item xs={12}>
                <Typography>SKU: {option?.sku}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>Product: {option?.product?.name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  Number Available: {option?.numAvailable}
                </Typography>
              </Grid>
            </Grid>
          )
        }
        }
      />
    </Grid>
    <Grid item xs={3}>
      <QuerySelectField
        name="batch"
        label="Batch"
        paginator={batchPaginator}
        CreateDialog={(props) => {
          return <BatchDialog {...props} allowSelectASN={false}/>
        }}
        createOnSave={() => batchPaginator.refresh()}
      />
    </Grid>
    <Grid item xs={2}>
      <NumberField
        name="num_units"
        label="Number of Units"
      />
    </Grid>
    <Grid item xs={2}>
      <BooleanField
        name="backorder"
        label="Backorder"
      />
    </Grid>
    <Grid item xs={1}>
      <Button
        onClick={()=>onRemove(listItemName)}
      >
        <CloseIcon /> Delete
      </Button>
    </Grid>
  </Grid>
}

export const ParcelOrderDialog = (props: Props) => {
  const classes = useStyles()
  const { open, handleClose, onSave, update, order } = props

  const [saving, setSaving] = useState(false)
  const [inventoryOpen, setInventoryOpen] = useState(true)

  const carrierPaginator = useQueryPaginator('carrier-list', skuit.client.carriers.list)
  const skuPaginator = useQueryPaginator('sku-list', skuit.client.skus.list, {
    params: { active: 'true' },
  })
  const customFieldPaginator = useQueryPaginator('custom-fields', skuit.client.parcel.orders.customFields.list)
  const customFieldValues: Record<string, string> = {}
  if (order?.customFields?.length) {
    for (const field of order.customFields) {
      customFieldValues[field.name || ''] = field.value || ''
    }
  }

  const orderUnitPaginator = useQueryPaginator('order-unit-list', skuit.client.parcel.orders.units.list, {
    params: { orders: order?.guid || 'or_noorder', 'on-container': 'false' },
  })

  const tagPaginator = useQueryPaginator('tags', skuit.client.attachments.tags.list)

  const { Form, Field, FieldList, data: formData } = useForm(orderSchema)

  const { mutate: submitSkus } = useMutation(skuit.client.parcel.orders.skuAvailability, {
    onSuccess: (response: AxiosResponse) => {
      const newInventory = response.data.map(
        (field: { sku: Sku; num_units: number; lookup: string }, index: number) => {
          const { sku, num_units, lookup } = field
          const skuObj = plainToClass(Sku, sku)
          const existing = formData.getValue('inventory')
          if (!skuObj) {
            formData.setError(
              `inventory[${index + existing.length}].sku`,
              `Invalid SKU lookup: ${lookup}`
            )
          } else if (num_units > (skuObj.numAvailable || 0)) {
            formData.setError(
              `inventory[${index + existing.length}].num_units`,
              'Not enough available units'
            )
          }
          return { sku: skuObj, num_units }
        }
      )
      formData.extend('inventory', newInventory)
    },
    onError: (error: AxiosError) => {
      if (skuFileInput.current) {
        skuFileInput.current.value = ''
      }
      if (error?.response?.data?.details) {
        toast.error(() => (
          <Notification message={`Failed to process CSV: ${error?.response?.data.details}`} />
        ))
      } else {
        toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
      }
    },
  })

  const skuFileInput = createRef<HTMLInputElement>()
  const onSkuFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const form = new FormData()
      form.append('file', e.target.files[0])
      submitSkus(form)
    }
  }

  const startingInventory = orderUnitPaginator.data?.results.length
    ? orderUnitPaginator.data.results.map((item: ParcelOrderUnit) => ({
        sku: item.sku,
        num_units: item.numUnits,
      }))
    : undefined

  const onSubmit = (data: OrderProps) => {
    setSaving(true)
    const attachments = data.attachments
    delete data.attachments

    const customFields = []
    for (const field of customFieldPaginator.data.results) {
      if (field.name in data) {
        customFields.push({ name: field.name, value: (data as any)[field.name] })
        delete (data as any)[field.name]
      }
    }
    if (customFields.length) {
      data['custom_fields'] = customFields
    }

    if (!data.inventory) {
      data.inventory = []
    }

    if (data.fulfill_on) {
      const fulfillOn = moment(data.fulfill_on)
      data.fulfill_on = fulfillOn.format("YYYY-MM-DD")
    }

    let func = skuit.client.parcel.orders.create
    let action = 'creat'
    if (update) {
      func = (data: OrderProps) => skuit.client.parcel.orders.patch(order?.guid || '', data)
      action = 'updat'
    }
    func(data)
      .then((newOrder: ParcelOrder) => {
        if (attachments?.length) {
          skuit.client.parcel.orders.attachments
            .create(newOrder.guid as string, { attachments })
            .then(() => {
              handleClose()
              if (onSave) {
                onSave()
              }
              toast.success(() => (
                <Notification
                  message={`Successfully ${action}ed Order: ${newOrder.name}`}
                  newObject={newOrder}
                />
              ))
              toast.success(() => (
                <Notification message={`Successfully added ${attachments.length} attachments!`} />
              ))
            })
            .catch((attachmentError: AxiosError) => {
              if (attachmentError?.response?.data?.detail) {
                toast.error(() => (
                  <Notification
                    message={`Error ${action}ing attachments on Order: ${attachmentError?.response?.data.detail}`}
                  />
                ))
              } else if (attachmentError?.response?.status === 500) {
                toast.error(() => (
                  <Notification
                    message={`Error ${action}ting attachments on Order: An unknown error has ocurred`}
                  />
                ))
              } else {
                toast.error(() => (
                  <Notification
                    message={`Error ${action}ting attachments on Shipment: An unknown error has ocurred`}
                  />
                ))
              }
              setApiErrors(formData.setError, attachmentError)
            })
        } else {
          handleClose()
          if (onSave) {
            onSave()
          }
          toast.success(() => (
            <Notification
              message={`Successfully ${action}ed Order: ${newOrder.name}`}
              newObject={newOrder}
            />
          ))
        }
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Order')

        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }
  let saveLabel: string
  if (orderUnitPaginator.loading) {
    saveLabel = 'Loading...'
  } else if (saving) {
    saveLabel = 'Saving...'
  } else {
    saveLabel = 'Save'
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{order ? 'Update' : 'Create'} Order</DialogTitle>
        <DialogContent>
          <TextField
            name="number"
            label="Order Number"
            defaultValue={order?.number}
          />
          <QuerySelectField
            name="carrier"
            label="Carrier"
            paginator={carrierPaginator}
            defaultValue={order ? order.carrier : undefined}
            getOptionLabel={(option) => `${option.name}${option.scac ? ' [' + option.scac + ']' : ''}`}
            renderOption={(option) => (
              <span>
                {option.name}
                {option.scac && ` [${option.scac}]`}
              </span>
            )}
          />
          <DateField
            name="fulfill_on"
            label="Date to Fulfill On"
            defaultValue={order?.fulfillOn}
          />
          <TextareaField
            name="details"
            label="Order Details"
            defaultValue={order?.details || ''}
          />
          <Typography variant="h6" align="center" mt={12}>
            Shipping
          </Typography>
          <AddressField
            name="address"
            defaultValue={order?.address}
          />
          {customFieldPaginator.data.results.map((field) => (
            <TextField
              key={field.name}
              name={field.name}
              label={field.label}
              defaultValue={customFieldValues[field.name]}
            />
          ))}

          <Typography variant="h6" mt={6} mb={3}>
            Attachments
          </Typography>
          <FieldList
            name="attachments"
            startCount={0}
            renderHeader={({ name, value, setValue }) => (
              <DropzoneAreaBase
                dropzoneText="Drag and drop or click to add files"
                fileObjects={[]}
                onAdd={(files) => {
                  const fileData = files.map((fileObj) => {
                    return { file: fileObj.file, tags: [] }
                  })
                  const newValue = (value || []).concat(fileData)
                  setValue(name, newValue)
                }}
                filesLimit={10}
                showPreviewsInDropzone={false}
              />
            )}
            renderList={ImageListWrapper}
            renderField={({ name: itemName }) => (
              <Grid item xs={3} key={itemName}>
                <Card>
                  <CardContent>
                    <Field
                      name="file"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <FilePreview file={value} onDelete={() => remove(itemName)} />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="url"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <UrlPreview
                              url={value}
                              allowDownload={false}
                              onDelete={() => remove(itemName)}
                            />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="tags"
                      getSubmitValue={(value) =>
                        value && value.map((tag: { slug: string }) => tag.slug)
                      }
                      renderField={({ name, value, setValue, error }) => (
                        <Autocomplete
                          id={name}
                          multiple
                          freeSolo
                          options={tagPaginator.data.results}
                          getOptionLabel={(tag) => tag.name}
                          filterSelectedOptions
                          onChange={(event: any, newValue: any | null) => setValue(name, newValue)}
                          value={value === '' ? [] : value}
                          renderInput={(params) => (
                            <MuiTextField
                              {...params}
                              error={!!error}
                              helperText={error}
                              label="Tags"
                            />
                          )}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          />

          <Typography variant="h6" mt={12}>
            Inventory{' '}
            <IconButton onClick={() => setInventoryOpen(!inventoryOpen)}>
              {inventoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Typography>
          <Collapse in={inventoryOpen}>
            {orderUnitPaginator.loading ? (
              <CircularProgress color="inherit" />
            ) : (
              <FieldList
                name="inventory"
                startCount={0}
                defaultValues={startingInventory}
                
                renderField={({ name: listItemName, data, value }) => {
                  return <InventoryListRow
                    value={value}
                    skuPaginator={skuPaginator}
                    listItemName={listItemName}
                    onRemove={(listItemName)=> {data.remove(listItemName)}}
                    />
                }}
              />
            )}
            <Centered>
              <Grid container justify="center" spacing={6}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    mt={6}
                    onClick={() =>
                      formData.append('inventory', { sku: undefined, num_units: undefined })
                    }
                  >
                    <AddIcon />
                    Add SKUs
                  </Button>
                </Grid>
                <Grid item>
                  <input
                    type="file"
                    accept=".csv"
                    ref={skuFileInput}
                    style={{ display: 'none' }}
                    onChange={onSkuFileSelected}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    mt={6}
                    onClick={() => {
                      if (skuFileInput.current) {
                        skuFileInput.current.click()
                      }
                    }}
                  >
                    <AddIcon />
                    Add SKUs by CSV
                  </Button>
                </Grid>
              </Grid>
            </Centered>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={
              saving ||
              orderUnitPaginator.loading
            }
            autoFocus
          >
            {saveLabel}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
